import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  Module,
  EntityAccess,
  Role,
  User,
  UserEntityAccess,
  Policy,
} from 'types/auth';

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  currentUserEntityRoleMap: Record<string, Role[]>; // entity_id -> roles
  usersEntityAccessList: UserEntityAccess[];
  allRoles: EntityAccess[];
  modules: Module[];
  userAccessibleModules: Module[];
  userPolicies: Policy[];
  modulesLoaded: boolean;
}

interface AuthStore extends AuthState {
  stopLoading: () => void;
  setUser: (user: (User & any) | null) => void;
  logout: () => void;
  initialize: () => void;
  setCurrentUserEntityRoleMap: (
    currentUserEntityRoleMap: Record<string, Role[]>
  ) => void;
  setUsersEntityAccessList: (usersEntityAccessList: UserEntityAccess[]) => void;
  setAllRoles: (allRoles: EntityAccess[]) => void;
  setModules: (modules: Module[]) => void;
  setUserAccessibleModules: (userAccessibleModules: Module[]) => void;
  setUserPolicies: (userPolicies: Policy[]) => void;
  resetModulesLoaded: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      loading: true,
      initialized: false,
      currentUserEntityRoleMap: {},
      usersEntityAccessList: [],
      allRoles: [],
      modules: [],
      userAccessibleModules: [],
      userPolicies: [],
      modulesLoaded: false,
      stopLoading: () => set({ loading: false }),
      setUser: (user) => set({ user, loading: false }),
      setCurrentUserEntityRoleMap: (currentUserEntityRoleMap) =>
        set({ currentUserEntityRoleMap }),
      setUsersEntityAccessList: (usersEntityAccessList) =>
        set((state) => {
          // Find and set current user's backend ID
          const currentUserAccess = state.user?.email
            ? usersEntityAccessList.find(
                (u) => u.user_email === state.user?.email
              )
            : undefined;
          return {
            usersEntityAccessList,
            // Update user.id if we found a match
            ...(currentUserAccess && state.user
              ? { user: { ...state.user, id: currentUserAccess.user_id } }
              : {}),
          };
        }),
      setAllRoles: (allRoles) => set({ allRoles }),
      setModules: (modules) => set({ modules }),
      setUserAccessibleModules: (userAccessibleModules) =>
        set({ userAccessibleModules, modulesLoaded: true }),
      setUserPolicies: (userPolicies) => set({ userPolicies }),
      resetModulesLoaded: () =>
        set({
          modulesLoaded: false,
          userAccessibleModules: [],
          userPolicies: [],
        }),
      logout: () => {
        // Clear user data from store
        set({ user: null, loading: false });

        // Clear access control data from store
        set({
          currentUserEntityRoleMap: {},
          usersEntityAccessList: [],
          allRoles: [],
          modules: [],
          userAccessibleModules: [],
          userPolicies: [],
          modulesLoaded: false,
        });

        // Clear auth-related stored data only
        localStorage.removeItem('auth-storage');
        sessionStorage.removeItem('auth-storage');

        // Also clear any other auth-related items that might exist
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
      },
      initialize: () => set({ initialized: true, loading: false }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user }),
      onRehydrateStorage: () => (state) => {
        // When the store is rehydrated, we want to initialize it
        if (state) {
          state.initialize();
        }
      },
    }
  )
);
